/**
 * SmartRenderer - Multi-Mode Intelligent Rendering System
 * 
 * This system renders elements based on smart layout decisions:
 * - Full text rendering
 * - Abbreviated text rendering  
 * - Icon-only rendering
 * - Conceptual bar representation
 * - External label rendering
 * - Hidden elements
 * 
 * CRITICAL: Only renders when smartLayout is enabled - zero impact otherwise
 */

'use client';

import React from 'react';
import type {
  ElementDecision,
  RenderPlan,
  Rectangle
} from './SmartLayout.types';
import { BRAND } from '../constants/brand';

interface SmartRendererProps {
  renderPlan: RenderPlan;
  containerDimensions: { width: number; height: number };
  className?: string;
}

/**
 * Main smart renderer component
 */
export function SmartRenderer({
  renderPlan,
  containerDimensions,
  className = ''
}: SmartRendererProps) {
  return (
    <g className={`smart-renderer ${className}`}>
      {/* Render all elements according to their decisions */}
      {renderPlan.elements.map((decision, index) => (
        <SmartElement
          key={decision.element.id || `smart-element-${index}`}
          decision={decision}
        />
      ))}
      
      {/* Render legend if needed */}
      {renderPlan.requiresLegend && (
        <SmartLegend 
          decisions={renderPlan.elements}
          containerDimensions={containerDimensions}
        />
      )}
      
      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && renderPlan.recommendations.length > 0 && (
        <SmartDebugOverlay recommendations={renderPlan.recommendations} />
      )}
    </g>
  );
}

/**
 * Renders individual element based on its decision
 */
function SmartElement({ decision }: { decision: ElementDecision }) {
  const { chosenMode, renderProps, element } = decision;
  
  // Skip hidden elements
  if (chosenMode === 'hidden' || (renderProps as any).visible === false) {
    return null;
  }
  
  const position = renderProps.position || { x: 0, y: 0, width: 100, height: 30 };
  const baseProps = {
    id: `smart-${element.id}`,
    className: `smart-element smart-${chosenMode}`,
  };

  // Type assertion for render props to handle dynamic properties
  const props = renderProps as any;

  switch (chosenMode) {
    case 'full-text':
    case 'abbreviated':
      return (
        <SmartTextRenderer
          {...baseProps}
          text={props.text || ''}
          position={position}
          fontSize={props.fontSize || 12}
          element={element}
        />
      );
      
    case 'icon-only':
      return (
        <SmartIconRenderer
          {...baseProps}
          icon={props.icon || '●'}
          position={position}
          element={element}
        />
      );
      
    case 'conceptual-bar':
      return (
        <SmartBarRenderer
          {...baseProps}
          position={position}
          barWidth={props.barWidth || 50}
          barHeight={props.barHeight || 8}
          element={element}
        />
      );
      
    case 'external-label':
      return (
        <SmartExternalLabelRenderer
          {...baseProps}
          text={props.text || ''}
          originalPosition={position}
          labelPosition={props.externalLabelPosition || position}
          fontSize={props.fontSize || 12}
          element={element}
        />
      );
      
    default:
      console.warn(`Unknown render mode: ${chosenMode}`);
      return null;
  }
}

/**
 * Text renderer for full-text and abbreviated modes
 */
function SmartTextRenderer({
  text,
  position,
  fontSize,
  element,
  id,
  className
}: {
  text: string;
  position: Rectangle;
  fontSize: number;
  element: any;
  id: string;
  className: string;
}) {
  const lines = splitTextIntoLines(text, position.width, fontSize);
  const lineHeight = fontSize * 1.2;
  
  return (
    <g id={id} className={className}>
      {/* Background if needed */}
      {element.content.contextType === 'button' && (
        <rect
          x={position.x}
          y={position.y}
          width={position.width}
          height={position.height}
          fill={BRAND.colors.primary}
          rx={4}
          opacity={0.1}
        />
      )}
      
      {/* Text lines */}
      {lines.map((line, index) => (
        <text
          key={index}
          x={position.x + position.width / 2}
          y={position.y + (index + 1) * lineHeight}
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={getTextColor(element) as string}
          className="smart-text"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/**
 * Icon renderer for icon-only mode
 */
function SmartIconRenderer({
  icon,
  position,
  element,
  id,
  className
}: {
  icon: string;
  position: Rectangle;
  element: any;
  id: string;
  className: string;
}) {
  const iconSize = Math.min(position.width * 0.6, position.height * 0.6, 24);
  
  return (
    <g id={id} className={className}>
      {/* Background for buttons */}
      {element.content.contextType === 'button' && (
        <rect
          x={position.x}
          y={position.y}
          width={position.width}
          height={position.height}
          fill={BRAND.colors.primary}
          rx={4}
          opacity={0.1}
        />
      )}
      
      {/* Icon */}
      <text
        x={position.x + position.width / 2}
        y={position.y + position.height / 2}
        fontSize={iconSize}
        textAnchor="middle"
        dominantBaseline="central"
        fill={getTextColor(element)}
        className="smart-icon"
      >
        {icon}
      </text>
    </g>
  );
}

/**
 * Bar renderer for conceptual representation
 */
function SmartBarRenderer({
  position,
  barWidth,
  barHeight,
  element,
  id,
  className
}: {
  position: Rectangle;
  barWidth: number;
  barHeight: number;
  element: any;
  id: string;
  className: string;
}) {
  return (
    <g id={id} className={className}>
      <rect
        x={position.x + (position.width - barWidth) / 2}
        y={position.y + (position.height - barHeight) / 2}
        width={barWidth}
        height={barHeight}
        fill={BRAND.colors.neutral}
        rx={2}
        opacity={0.6}
        className="smart-conceptual-bar"
      />
    </g>
  );
}

/**
 * External label renderer
 */
function SmartExternalLabelRenderer({
  text,
  originalPosition,
  labelPosition,
  fontSize,
  element,
  id,
  className
}: {
  text: string;
  originalPosition: Rectangle;
  labelPosition: Rectangle;
  fontSize: number;
  element: any;
  id: string;
  className: string;
}) {
  return (
    <g id={id} className={className}>
      {/* Original element as simplified representation */}
      <rect
        x={originalPosition.x}
        y={originalPosition.y}
        width={originalPosition.width}
        height={originalPosition.height}
        fill={BRAND.colors.neutral}
        opacity={0.3}
        rx={2}
        className="smart-placeholder"
      />
      
      {/* Leader line */}
      <line
        x1={originalPosition.x + originalPosition.width}
        y1={originalPosition.y + originalPosition.height / 2}
        x2={labelPosition.x}
        y2={labelPosition.y + labelPosition.height / 2}
        stroke={BRAND.colors.neutral}
        strokeWidth={1}
        strokeDasharray="3,3"
        className="smart-leader-line"
      />
      
      {/* External label */}
      <text
        x={labelPosition.x}
        y={labelPosition.y + labelPosition.height / 2}
        fontSize={fontSize}
        dominantBaseline="central"
        fill={getTextColor(element)}
        className="smart-external-label"
      >
        {text}
      </text>
    </g>
  );
}

/**
 * Legend for conceptual elements and external labels
 */
function SmartLegend({
  decisions,
  containerDimensions
}: {
  decisions: ElementDecision[];
  containerDimensions: { width: number; height: number };
}) {
  const legendItems = decisions.filter(d => 
    d.chosenMode === 'conceptual-bar' || 
    d.chosenMode === 'icon-only'
  );
  
  if (legendItems.length === 0) return null;
  
  const legendY = containerDimensions.height - 60;
  const legendX = 10;
  
  return (
    <g className="smart-legend">
      {/* Legend background */}
      <rect
        x={legendX - 5}
        y={legendY - 5}
        width={200}
        height={legendItems.length * 20 + 20}
        fill={BRAND.colors.backgroundAlt}
        stroke={BRAND.colors.border}
        rx={4}
        opacity={0.9}
      />
      
      {/* Legend title */}
      <text
        x={legendX}
        y={legendY + 12}
        fontSize={10}
        fontWeight="bold"
        fill={BRAND.colors.text.primary}
      >
        Legend
      </text>
      
      {/* Legend items */}
      {legendItems.map((decision, index) => (
        <g key={decision.element.id} className="legend-item">
          {decision.chosenMode === 'conceptual-bar' ? (
            <rect
              x={legendX}
              y={legendY + 25 + index * 20}
              width={20}
              height={8}
              fill={BRAND.colors.neutral}
              opacity={0.6}
              rx={2}
            />
          ) : (
            <text
              x={legendX + 10}
              y={legendY + 30 + index * 20}
              fontSize={12}
              textAnchor="middle"
              dominantBaseline="central"
              fill={BRAND.colors.text.primary}
            >
              {(decision.renderProps as any).icon}
            </text>
          )}
          
          <text
            x={legendX + 30}
            y={legendY + 30 + index * 20}
            fontSize={9}
            dominantBaseline="central"
            fill={BRAND.colors.text.primary}
          >
            {decision.element.content.text?.substring(0, 20) || 'Element'}
            {decision.element.content.text && decision.element.content.text.length > 20 ? '...' : ''}
          </text>
        </g>
      ))}
    </g>
  );
}

/**
 * Debug overlay for development
 */
function SmartDebugOverlay({ recommendations }: { recommendations: string[] }) {
  return (
    <g className="smart-debug-overlay">
      <rect
        x={10}
        y={10}
        width={300}
        height={recommendations.length * 15 + 30}
        fill="rgba(255, 255, 0, 0.1)"
        stroke="orange"
        strokeWidth={1}
        rx={4}
      />
      
      <text x={15} y={25} fontSize={10} fontWeight="bold" fill="orange">
        Smart Layout Recommendations:
      </text>
      
      {recommendations.map((rec, index) => (
        <text
          key={index}
          x={15}
          y={40 + index * 15}
          fontSize={8}
          fill="orange"
        >
          • {rec}
        </text>
      ))}
    </g>
  );
}

/**
 * Utility functions
 */
function splitTextIntoLines(text: string, maxWidth: number, fontSize: number): string[] {
  const avgCharWidth = fontSize * 0.55;
  const charsPerLine = Math.floor(maxWidth / avgCharWidth);
  
  if (charsPerLine <= 0) return [text];
  
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= charsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word longer than line - break it
        lines.push(word.substring(0, charsPerLine));
        currentLine = word.substring(charsPerLine);
      }
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines.length > 0 ? lines : [text];
}

function getTextColor(element: any): string {
  if (element.content.priority === 'critical') {
    return BRAND.colors.featured; // Pink for critical
  }
  if (element.content.contextType === 'header') {
    return BRAND.colors.text.primary; // Standard gray for headers
  }
  return BRAND.colors.text.primary; // Default text color
}

/**
 * Hook for using smart renderer in components
 */
export function useSmartRenderer(enabled: boolean = false) {
  return {
    isEnabled: enabled,
    SmartRenderer: enabled ? SmartRenderer : null
  };
}